import { Stack, styled } from '@mui/material'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCreateOrderOption,
  useCurrentSpotSymbol,
  useSpotFeeRates,
  useSpotOrderDirection,
} from 'store/spot/hooks'
import { OrderDirection } from 'store/spot/types'
import { Flex } from 'styles'
import { e2w, getEffectiveDecimals, isZero, toFixed, w2e } from 'utils/number'
import { useSpotTradeAvailableBalance } from '../../../../hooks/spot/useSpotTradeAvailableBalance'
import { useTradingFeeRate } from '../../../../hooks/useTradingFees'
import { formatUnits } from 'ethers/lib/utils'
import numbro from 'numbro'
import { TRADE_FEE_RATE_SCALE } from '../../../../constants/trade'
import { useCurrencyByName } from '../../../../store/market/hooks'
import Decimal from 'decimal.js'
import { TooltipText } from 'components/TooltipText'

const Total = styled(Flex)`
  justify-content: space-between;
  font-weight: 400;
  font-size: 12px;
  align-items: flex-start;
  line-height: 20px;
  color: ${(props) => props.theme.color.text70};
`
const TotalTitle = styled('div')`
  span {
    color: ${(props) => props.theme.color.text40};
  }
`

const MinReceiveText = styled(TotalTitle)`
  color: rgba(123, 136, 145, 1);
`
const TotalValue = styled(Flex)`
  flex: 1;
  flex-wrap: wrap;
  text-align: right;
  font-size: 14px;
  color: rgba(229, 234, 238, 1);
  div {
    width: 100%;
    font-weight: 400;
  }
`
const TotalSecondFee = styled('div')`
  color: ${(props) => props.theme.color.text40};
`
const Available = styled(TotalTitle)`
  display: flex;
  gap: 6px;
  color: rgb(123, 136, 145);
`

const largePriScale = 6
function getDisplayScale(
  isBuy: boolean,
  baseScale: number,
  quoteScale: number,
) {
  if (baseScale < largePriScale) {
    baseScale = largePriScale
  }
  if (isBuy) {
    return {
      pri: baseScale,
      sec: quoteScale,
    }
  } else {
    return {
      pri: quoteScale,
      sec: baseScale,
    }
  }
}

export const TotalAndFee = memo(() => {
  const orderDirection = useSpotOrderDirection()
  const isBuy = orderDirection === OrderDirection.LONG
  const {
    baseCurrency = '',
    quoteCurrency = '',
    baseScale = 0,
    quoteScale = 0,
  } = useCurrentSpotSymbol()
  const feeRates = useSpotFeeRates()
  const { limitPrice, baseCurrencyAmount } = useCreateOrderOption()
  const quoteToken = useCurrencyByName(quoteCurrency)
  const basedToken = useCurrencyByName(baseCurrency)
  const priSymbol = isBuy ? basedToken?.name : quoteToken?.name
  const secSymbol = isBuy ? quoteToken?.name : basedToken?.name

  const displayScale = getDisplayScale(isBuy, baseScale, quoteScale)

  const tradingFeeRates = useTradingFeeRate(
    limitPrice,
    baseCurrencyAmount,
    feeRates?.maker,
    feeRates?.taker,
  )

  const available = useSpotTradeAvailableBalance(
    isBuy ? quoteCurrency : baseCurrency,
  )

  const priAmount = useMemo(() => {
    if (isZero(limitPrice) || isZero(baseCurrencyAmount)) {
      return '0'
    }
    const safeAmount = toFixed(baseCurrencyAmount)
    const safePrice = toFixed(limitPrice)
    return isBuy ? safeAmount : Decimal.mul(safeAmount, safePrice).toFixed()
  }, [isBuy, baseCurrencyAmount, limitPrice])
  const secAmount = useMemo(() => {
    if (isZero(limitPrice) || isZero(baseCurrencyAmount)) {
      return '0'
    }
    const safeAmount = toFixed(baseCurrencyAmount)
    const safePrice = toFixed(limitPrice)
    return isBuy ? Decimal.mul(safeAmount, safePrice).toFixed() : safeAmount
  }, [isBuy, baseCurrencyAmount, limitPrice])

  const priFeeAmount = useMemo(() => {
    if (isZero(priAmount)) return '0'
    return w2e(
      e2w(priAmount).mul(tradingFeeRates.taker).div(TRADE_FEE_RATE_SCALE),
    )
  }, [priAmount, tradingFeeRates.taker])

  const secFeeAmount = useMemo(() => {
    if (isZero(secAmount)) {
      return '0'
    }
    return w2e(
      e2w(secAmount).mul(tradingFeeRates.taker).div(TRADE_FEE_RATE_SCALE),
    )
  }, [secAmount, tradingFeeRates.taker])

  const priReceivedAmount = useMemo(() => {
    return w2e(e2w(priAmount).sub(e2w(priFeeAmount)))
  }, [priAmount, priFeeAmount])
  const secReceivedAmount = useMemo(() => {
    return w2e(e2w(secAmount).sub(e2w(secFeeAmount)))
  }, [secAmount, secFeeAmount])

  const balance = w2e(available ?? '0', isBuy ? quoteScale : baseScale)

  return (
    <Stack gap={'12px'} id="zk_account_balance">
      <Total>
        <Available>Available</Available>
        <TotalValue>
          <div>
            {balance} {secSymbol}
          </div>
        </TotalValue>
      </Total>
      <Total>
        <TotalTitle>
          <TooltipText
            title={`
              Maker: ${numbro(
                formatUnits(String(tradingFeeRates.maker! ?? 0), 4),
              ).format({
                output: 'percent',
                mantissa: 2,
              })}, 
              Taker: ${numbro(
                formatUnits(String(tradingFeeRates.taker! ?? 0), 4),
              ).format({
                output: 'percent',
                mantissa: 2,
              })}
          `}
            placement="left-start"
          >
            Est. Fee
          </TooltipText>
        </TotalTitle>
        <TotalValue>
          <div>
            {getEffectiveDecimals(priFeeAmount, displayScale.pri)} {priSymbol} (
            {getEffectiveDecimals(secFeeAmount, displayScale.sec)} {secSymbol})
          </div>
        </TotalValue>
      </Total>
      <Total>
        <MinReceiveText>Minimum received</MinReceiveText>
        <TotalValue>
          <div>
            {getEffectiveDecimals(priReceivedAmount, displayScale.pri)}{' '}
            {priSymbol} (
            {getEffectiveDecimals(secReceivedAmount, displayScale.sec)}{' '}
            {secSymbol})
          </div>
          <TotalSecondFee></TotalSecondFee>
        </TotalValue>
      </Total>
    </Stack>
  )
})
